import axios from "axios";
import { LEVEL_RANK, moduleForPath } from "@/admin/modules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("c7_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Client-side access-control enforcement: on an /admin page, block write
  // requests that exceed the current module's granted level (view → no writes,
  // edit → no delete). Backend re-checks the generic resource layer as well.
  try {
    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const path = window.location.pathname;
      const perms = window.__c7Perms;
      if (path.startsWith("/admin") && perms?.ready) {
        const mod = moduleForPath(path);
        if (mod) {
          const lvl = perms.levelFor(mod);
          const need = method === "delete" ? LEVEL_RANK.full : LEVEL_RANK.edit;
          if ((LEVEL_RANK[lvl] ?? 3) < need) {
            const err = new Error(
              lvl === "view"
                ? "You have view-only access to this module."
                : "You don't have permission to perform this action.",
            );
            err.__c7Blocked = true;
            return Promise.reject(err);
          }
        }
      }
    }
  } catch { /* never block on interceptor errors */ }

  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // A genuine 401 (token expired / session revoked server-side) → sign the
    // user out gracefully via the /session-expired screen. Network errors and
    // 5xx during a backend restart carry NO `err.response`, so they never
    // trigger a logout — the workspace connection banner + auto-recovery
    // handle those transient outages instead.
    const path = window.location.pathname;
    if (err.response?.status === 401 && path !== "/login" && path !== "/session-expired") {
      localStorage.removeItem("c7_token");
      window.location.href = "/session-expired";
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatErr(e) {
  const d = e?.response?.data?.detail ?? e?.message;
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return String(d);
}
